import { useMemo } from "react";

import type { SpecificationResponse } from "@btnet/services-btnet/handlers/models/specificationResponse";
import { SpecificationStatus } from "@btnet/services-btnet/handlers/models/specificationStatus";

import { usePermission } from "helpers/globalContext/usePermission";

import { canShowSaveToCatalogButton } from "entity/specification/common/saveSpecificationToCatalog";

import { type ISpecificationPanelConfig } from "./specificationPanelConfig";

/**
 * Pure: whether view-mode action buttons should be considered for display. False when the viewer
 * is in edit mode, in external/share mode, or looking at a historical snapshot.
 */
export function areActionsVisibleInMode({
    isExternalMode,
    isEditMode,
    selectedSnapshotId,
    isReadonly = false,
}: {
    isExternalMode: boolean;
    isEditMode: boolean;
    selectedSnapshotId: string | null;
    /** A fully read-only panel (READONLY_PANEL_CONFIG) surfaces no view-mode actions at all. */
    isReadonly?: boolean;
}) {
    return !isExternalMode && !isEditMode && !selectedSnapshotId && !isReadonly;
}

/** Pure: edit button visibility. */
export function canShowEditButton({
    spec,
    canUpdate,
    actionsVisible,
}: {
    spec: SpecificationResponse | undefined;
    canUpdate: boolean;
    actionsVisible: boolean;
}) {
    return actionsVisible && canUpdate && !!spec?.canEdit;
}

/** Pure: delete button visibility. */
export function canShowDeleteButton({
    spec,
    canDelete,
    actionsVisible,
}: {
    spec: SpecificationResponse | undefined;
    canDelete: boolean;
    actionsVisible: boolean;
}) {
    return actionsVisible && canDelete && !!spec?.canDelete;
}

/** Pure: print button visibility. Hidden in the mobile webview and for general-job specs. */
export function canShowPrintButton({
    isMobileWebview,
    actionsVisible,
    isGeneralJob,
}: {
    isMobileWebview: boolean;
    actionsVisible: boolean;
    isGeneralJob: boolean;
}) {
    return actionsVisible && !isMobileWebview && !isGeneralJob;
}

/**
 * Pure: publish button visibility. Publish only shows for drafts OR specs with unpublished changes
 * (re-publish).
 */
export function canShowPublishButton({
    spec,
    canUpdate,
    actionsVisible,
    isDraft,
    isGeneralJob,
}: {
    spec: SpecificationResponse | undefined;
    canUpdate: boolean;
    actionsVisible: boolean;
    isDraft: boolean;
    isGeneralJob: boolean;
}) {
    return (
        actionsVisible &&
        canUpdate &&
        !isGeneralJob &&
        !!spec?.canEdit &&
        (isDraft || !!spec?.hasUnpublishedChanges)
    );
}

/** Pure: unpublish (move to draft) button visibility. Only shown on published specs the user can edit; never for general-job specs. */
export function canShowUnpublishButton({
    spec,
    canUpdate,
    actionsVisible,
    isPublished,
    isGeneralJob,
}: {
    spec: SpecificationResponse | undefined;
    canUpdate: boolean;
    actionsVisible: boolean;
    isPublished: boolean;
    isGeneralJob: boolean;
}) {
    return actionsVisible && canUpdate && isPublished && !!spec?.canEdit && !isGeneralJob;
}

/** Pure: viewing-permissions button visibility. Never shown for general-job specs. */
export function canShowViewingPermissionsButton({
    spec,
    actionsVisible,
    isPublished,
    isGeneralJob,
}: {
    spec: SpecificationResponse | undefined;
    actionsVisible: boolean;
    isPublished: boolean;
    isGeneralJob: boolean;
}) {
    return actionsVisible && !!spec?.canShare && isPublished && !isGeneralJob;
}

export { canShowSaveToCatalogButton };

/**
 * Pure: per-spec share button visibility. Only for published specs; never external mode or
 * general-job. Panel mode hides it unless the panel config opts in — the linked-specs drawer
 * (#283821) offers no per-spec sharing, since the user is there to reference a spec in the context
 * of another record rather than distribute it. Book view (non-panel) always shows it.
 */
export function canShowShareButton({
    actionsVisible,
    isPublished,
    isGeneralJob,
    isPanelMode,
    showShare,
}: {
    actionsVisible: boolean;
    isPublished: boolean;
    isGeneralJob: boolean;
    isPanelMode: boolean;
    showShare: boolean;
}) {
    return actionsVisible && isPublished && !isGeneralJob && (!isPanelMode || showShare);
}

/**
 * Pure: version history button visibility. Available for builders only, in all non-external modes.
 * Panel mode hides it unless the panel config opts into version history (the job-level AG Grid
 * panel); book view (non-panel) always shows it.
 */
export function canShowVersionHistoryButton({
    isBuilder,
    isExternalMode,
    isEditMode,
    isPanelMode,
    showVersionHistory,
}: {
    isBuilder: boolean;
    isExternalMode: boolean;
    isEditMode: boolean;
    isPanelMode: boolean;
    showVersionHistory: boolean;
}) {
    return isBuilder && !isExternalMode && !isEditMode && (!isPanelMode || showVersionHistory);
}

/** Pure: unpublished-changes banner visibility. */
export function canShowUnpublishedChangesBanner({
    spec,
    isPublished,
    isBuilder,
    isExternalMode,
    isEditMode,
    selectedSnapshotId,
}: {
    spec: SpecificationResponse | undefined;
    isPublished: boolean;
    isBuilder: boolean;
    isExternalMode: boolean;
    isEditMode: boolean;
    selectedSnapshotId: string | null;
}) {
    return (
        isPublished &&
        !!spec?.hasUnpublishedChanges &&
        isBuilder &&
        !isExternalMode &&
        !isEditMode &&
        !selectedSnapshotId
    );
}

interface IUseSpecificationActionVisibilityParams {
    specification: SpecificationResponse | undefined;
    isExternalMode: boolean;
    isEditMode: boolean;
    isMobileWebview: boolean;
    isPanelMode: boolean;
    /**
     * The panel's capability config. In panel mode, `versionHistory` drives the VH button and
     * `!canEdit` (a fully read-only panel, e.g. READONLY_PANEL_CONFIG) suppresses the entire
     * view-mode action set. Ignored outside panel mode (book view always shows both).
     */
    panelConfig: ISpecificationPanelConfig;
    isBuilder: boolean;
    selectedSnapshotId: string | null;
    isGeneralJob: boolean;
    isGlobalSpecCatalogEnabled: boolean;
}

/**
 * Composes visibility flags for the SpecificationViewer's action-menu buttons. Version-history-button
 * and unpublished-changes-banner visibility are siblings, not part of this hook — call
 * {@link canShowVersionHistoryButton} / {@link canShowUnpublishedChangesBanner} directly.
 */
export function useSpecificationActionVisibility({
    specification,
    isExternalMode,
    isEditMode,
    isMobileWebview,
    isPanelMode,
    panelConfig,
    isBuilder,
    selectedSnapshotId,
    isGeneralJob,
    isGlobalSpecCatalogEnabled,
}: IUseSpecificationActionVisibilityParams) {
    const canUpdate = usePermission("specifications", "canUpdate");
    const canDelete = usePermission("specifications", "canDelete");
    const canAdd = usePermission("specifications", "canAdd");

    return useMemo(() => {
        const isDraft = specification?.status === SpecificationStatus.Draft;
        const isPublished = specification?.status === SpecificationStatus.Published;
        const isReadonly = isPanelMode && !panelConfig.canEdit;
        const actionsVisible = areActionsVisibleInMode({
            isExternalMode,
            isEditMode,
            selectedSnapshotId,
            isReadonly,
        });

        const showEdit = canShowEditButton({ spec: specification, canUpdate, actionsVisible });
        const showDelete = canShowDeleteButton({
            spec: specification,
            canDelete,
            actionsVisible,
        });
        const showPrint = canShowPrintButton({ isMobileWebview, actionsVisible, isGeneralJob });
        const showPublish = canShowPublishButton({
            spec: specification,
            canUpdate,
            actionsVisible,
            isDraft,
            isGeneralJob,
        });
        const showUnpublish = canShowUnpublishButton({
            spec: specification,
            canUpdate,
            actionsVisible,
            isPublished,
            isGeneralJob,
        });
        const showViewingPermissions = canShowViewingPermissionsButton({
            spec: specification,
            actionsVisible,
            isPublished,
            isGeneralJob,
        });
        const showShare = canShowShareButton({
            actionsVisible,
            isPublished,
            isGeneralJob,
            isPanelMode,
            showShare: panelConfig.share,
        });
        const showSaveToCatalog = canShowSaveToCatalogButton({
            isBuilder,
            canAdd,
            actionsVisible,
            isGeneralJob,
            isGlobalSpecCatalogEnabled,
        });

        return {
            isDraft,
            isPublished,
            showEdit,
            showDelete,
            showPrint,
            showPublish,
            showUnpublish,
            showViewingPermissions,
            showShare,
            showSaveToCatalog,
            showActionsMenu:
                showDelete ||
                showPrint ||
                showUnpublish ||
                showViewingPermissions ||
                showPublish ||
                showSaveToCatalog,
        };
    }, [
        specification,
        isExternalMode,
        isEditMode,
        isMobileWebview,
        isPanelMode,
        panelConfig,
        isBuilder,
        isGeneralJob,
        isGlobalSpecCatalogEnabled,
        selectedSnapshotId,
        canUpdate,
        canDelete,
        canAdd,
    ]);
}
